import { useCallback, useEffect, useMemo, useState } from "react";
import logo from "./assets/logo.webp";
import homeBeijingImage from "./assets/home-beijing.jpg";
import homeChongqingImage from "./assets/home-chongqing.jpg";
import homeGuangzhouImage from "./assets/home-guangzhou.jpg";
import homeShanghaiImage from "./assets/home-shanghai.jpg";
import wukangImage from "./assets/wukang.png";
import pmImage from "./assets/pm.jpg";
import shanghaiImage from "./assets/shanghai.png";
import zhangjiajieImage from "./assets/zhangjiajie-forest.png";

import BlogDetailPage from "./tabs/BlogDetailPage";
import BlogTab from "./tabs/BlogTab";
import BalancePaymentPage from "./tabs/BalancePaymentPage";
import BookingPage, { type BookingPaymentResult } from "./tabs/BookingPage";
import BookingPayPalReturnPage from "./tabs/BookingPayPalReturnPage";
import BookingPaymentResultPage from "./tabs/BookingPaymentResultPage";
import CompanyTab from "./tabs/CompanyTab";
import ContactTab from "./tabs/ContactTab";
import HomeTab from "./tabs/HomeTab";
import ShopTab from "./tabs/ShopTab";
import TravelDetailPage from "./tabs/TravelDetailPage";
import TravelTab from "./tabs/TravelTab";
import { APP_CONFIG } from "./config/runtime";
import type { BlogArticleConfig } from "./types/blog";
import type { TravelDetailConfig, TravelDetailData } from "./types/travelDetail";

const TRAVEL_API_BASE_URL = APP_CONFIG.apiBaseUrl.replace(/\/+$/, "");
const TURNSTILE_SITE_KEY = APP_CONFIG.turnstileSiteKey.trim();
const PAYPAL_CLIENT_ID = APP_CONFIG.paypalClientId.trim();
const WHATSAPP_CHAT_URL = `https://wa.me/8617318902873?text=${encodeURIComponent(
  "Hi Fulcrum Travel, I want to customize a trip."
)}`;

type PublicTravelConfig = Omit<TravelDetailConfig, "locale"> & {
  locale?: string;
};

type PublicTravelItem = {
  id: string;
  config: PublicTravelConfig;
};

type PublicBlogItem = {
  id: string;
  slug: string;
  config: BlogArticleConfig;
};

type PublicBlogListResponse = {
  success: boolean;
  data?: {
    items: PublicBlogItem[];
    pagination?: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
  };
  error?: string;
};

type PublicBlogDetailResponse = {
  success: boolean;
  data?: {
    item: PublicBlogItem;
  };
  error?: string;
};

type FooterContactPopover = "wechat" | "phone" | null;

type PublicTravelFacets = {
  destinations: string[];
  topics: string[];
  durationDays: number[];
  maxPrice: number;
  minDurationDays: number;
  maxDurationDays: number;
};

type PublicTravelListResponse = {
  success: boolean;
  data?: {
    items: PublicTravelItem[];
    pagination?: {
      page: number;
      pageSize: number;
      total: number;
      totalPages: number;
    };
    facets?: PublicTravelFacets;
  };
  error?: string;
};

const navItems = ["Home", "Travel", "Company", "Blog", "Contact Us"];

const slides = [
  {
    id: "desert",
    eyebrow: "The Best",
    title: "China Travel",
    subtitle: "Red canyon roads, starlit skies, and wide-open horizons.",
    highlights: ["7 Days / 6 Nights", "Private Guide", "From $1,280"],
    image: wukangImage
  },
  {
    id: "coast",
    eyebrow: "NEW ROUTE",
    title: "Island Breeze",
    subtitle: "Turquoise bays, hidden coves, and slow mornings on the sea.",
    highlights: ["5 Days / 4 Nights", "Boutique Stays", "From $1,050"],
    image: pmImage
  },
  {
    id: "mountain",
    eyebrow: "ADVENTURE",
    title: "Alpine Escape",
    subtitle: "Snowy ridgelines, cozy chalets, and winter trails.",
    highlights: ["8 Days / 7 Nights", "Ski Pass Included", "From $1,980"],
    image: shanghaiImage
  }
];

const fallbackHomeHighlights = [
  {
    id: "city-walks",
    title: "City Walks Tour",
    image: shanghaiImage,
    accentClass: "bg-[#f7b500]",
    iconClass: "text-white",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-7 w-7"
      >
        <path
          d="M4 18H20"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M6 18V10.5L10.5 7L15 10.5V18"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M10 18V13H13.5V18"
          stroke="currentColor"
          strokeWidth="1.8"
        />
      </svg>
    )
  },
  {
    id: "electric-bikes",
    title: "Electric Bikes",
    image: pmImage,
    accentClass: "bg-[#0b1c2b]",
    iconClass: "text-[#f7b500]",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-7 w-7"
      >
        <circle cx="6.5" cy="17.5" r="3.5" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="17.5" cy="17.5" r="3.5" stroke="currentColor" strokeWidth="1.8" />
        <path
          d="M6.5 17.5L10.5 10H14.5L12.5 13.5H16.5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.5 10L16.5 6.5H20"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    )
  },
  {
    id: "skyscrapers",
    title: "Skyscrapers View",
    image: wukangImage,
    accentClass: "bg-[#f7b500]",
    iconClass: "text-white",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-7 w-7"
      >
        <path
          d="M4 20H20"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M7 20V9H11V20"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M13 20V6H17V20"
          stroke="currentColor"
          strokeWidth="1.8"
        />
      </svg>
    )
  }
];

const fallbackHomeDestinations = [
  {
    id: "beijing",
    title: "Beijing",
    country: "China",
    summary: "[Explore Beijing] 4 Days and 3 Nights in Beijing · Mich [...].",
    durationLabel: "4-Days 3-Nights",
    price: "999 $",
    oldPrice: "1099 $",
    badge: "SALE",
    image: wukangImage
  },
  {
    id: "chengdu",
    title: "Chengdu",
    country: "China",
    summary: "[Ba Style and Shu Charm, Savoring Sichuan] Chengdu + Qi [...].",
    durationLabel: "5-Days 4-Nights",
    price: "899 $",
    oldPrice: "999 $",
    badge: "SALE",
    image: zhangjiajieImage
  },
  {
    id: "guangzhou",
    title: "Guangzhou",
    country: "China",
    summary: "[Luxury Flavors of Cantonese Charm · One City, One Inta [...].",
    durationLabel: "5-Days 4-Nights",
    price: "799 $",
    oldPrice: "899 $",
    badge: "SALE",
    image: shanghaiImage
  }
];

const homeWorldwideDestinations = [
  {
    id: "guangzhou-world",
    title: "Guangzhou",
    subtitle: "(Minimum order for 2 people)",
    image: homeGuangzhouImage
  },
  {
    id: "shanghai-world",
    title: "Shanghai",
    subtitle: "(Minimum order for 2 people)",
    image: homeShanghaiImage
  },
  {
    id: "beijing-world",
    title: "Beijing",
    subtitle: "(Minimum order for 2 people)",
    image: homeBeijingImage
  },
  {
    id: "chongqing-world",
    title: "Chongqing",
    subtitle: "",
    image: homeChongqingImage
  }
];

const adventureHighlights = [
  "Connect with nature",
  "Experience other cultures",
  "Create unforgettable memories"
];

const homeBlogPosts = {
  featured: {
    id: "zhangjiajie-feature",
    title: "A Historical Overview of Zhangjiajie",
    date: "2025年12月8日",
    image: zhangjiajieImage
  },
  items: [
    {
      id: "beijing-home",
      title: "A Historical Overview of Beijing",
      date: "2025年12月8日",
      image: shanghaiImage
    },
    {
      id: "chengdu-home",
      title: "A Historical Overview of Chengdu",
      date: "2025年12月8日",
      image: pmImage
    },
    {
      id: "guangzhou-home",
      title: "A Historical Overview of Guangzhou",
      date: "2025年12月8日",
      image: wukangImage
    },
    {
      id: "jiuzhaigou-home",
      title: "A Historical Overview of Jiuzhaigou",
      date: "2025年12月8日",
      image: zhangjiajieImage
    }
  ]
};

const contacts = [
  "Start Your Customized Journey Now",
  "WeChat: Fulcrum Travel",
  "WhatsApp: +86 173 1890 2873",
  "Phone Number: +86 173 1890 2873",
  "Email: fulcrumtravel@gmail.com"
];

const contactUsCards = [
  {
    title: "Location",
    subtitle: "Travel Agency",
    description:
      "Address: Room 606, 6th Floor, Debi WE, No. 168, Tianfu 5th Street, High-tech Zone, Chengdu City, Sichuan Province, China",
    icon: (
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#fff7e2] text-[#f3b455]">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
        >
          <path
            d="M12 13.5C13.6569 13.5 15 12.1569 15 10.5C15 8.84315 13.6569 7.5 12 7.5C10.3431 7.5 9 8.84315 9 10.5C9 12.1569 10.3431 13.5 12 13.5Z"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <path
            d="M5 10.5C5 6.91015 8.13401 4 12 4C15.866 4 19 6.91015 19 10.5C19 15.5 12 20 12 20C12 20 5 15.5 5 10.5Z"
            stroke="currentColor"
            strokeWidth="1.6"
          />
        </svg>
      </span>
    )
  },
  {
    title: "Give us a call",
    subtitle: "Mobile Number",
    description: "Forest Wang (Tour Agency): +86 17318902873",
    icon: (
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#f3b455] text-white">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
        >
          <path
            d="M5.5 4.5H8.5L9.5 8L7.5 9.5C8.7 12.1 10.9 14.3 13.5 15.5L15 13.5L18.5 14.5V17.5C18.5 18.6046 17.6046 19.5 16.5 19.5C10.4249 19.5 5.5 14.5751 5.5 8.5C5.5 7.39543 6.39543 6.5 7.5 6.5H5.5V4.5Z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    )
  },
  {
    title: "Write for anything",
    subtitle: "Quotes",
    description:
      "Write to this email for a detailed quotation: fulcrumtravel@gmail.com and information.",
    icon: (
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#0b1c2b] text-white">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
        >
          <path
            d="M4 6H20V18H4V6Z"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <path
            d="M4 7L12 12L20 7"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    )
  }
];

type TravelPackage = {
  id: string;
  title: string;
  country: string;
  destination?: string;
  summary: string;
  durationLabel: string;
  durationDays?: number;
  topics?: string[];
  price: string;
  oldPrice?: string;
  badge?: string;
  image: string;
};

const travelPackages: TravelPackage[] = [
  {
    id: "jiuzhaigou",
    title: "Jiuzhaigou",
    country: "China",
    summary:
      "[Blue Ice and Mysterious Waterfall, Frost and Snow's Splendor] 4-Day, 3-Night Jiuzhaigou Tour [...].",
    durationLabel: "4 Days",
    price: "1999 $",
    oldPrice: "2199 $",
    badge: "SALE",
    image: pmImage
  },
  {
    id: "shanghai",
    title: "Shanghai",
    country: "China",
    summary:
      "[Modern Shanghai, Century of Splendor] 2-Day, 1-Night Classic Shanghai Cultural Tour - A Must-choos [...].",
    durationLabel: "2- Day 1-Night",
    price: "499 $",
    oldPrice: "699 $",
    badge: "SALE",
    image: shanghaiImage
  },
  {
    id: "xian",
    title: "Xi'an",
    country: "China",
    summary:
      "[National Treasures, Cultural Heritage] 4-Day, 3-Night Classic Tour of Xi'an: A Thousand-Year [...].",
    durationLabel: "4-Days 3-Nights",
    price: "799 $",
    oldPrice: "899 $",
    badge: "SALE",
    image: wukangImage
  },
  {
    id: "shangrila",
    title: "Chongqing",
    country: "China",
    summary:
      "[Famous Landscape City] 5-Day 4-Night World Heritage Tour of Chongqing + Wulong Experience the highl [...].",
    durationLabel: "5-Days 4-Nights",
    price: "499 $",
    oldPrice: "599 $",
    badge: "SALE",
    image: pmImage
  },
  {
    id: "guilin",
    title: "Guilin",
    country: "China",
    summary:
      "[Limestone Wonders, Li River Cruise] 4-Day, 3-Night Guilin Highlights + Yangshuo Escape [...].",
    durationLabel: "4-Days 3-Nights",
    price: "699 $",
    oldPrice: "899 $",
    badge: "SALE",
    image: shanghaiImage
  },
  {
    id: "hangzhou",
    title: "Hangzhou",
    country: "China",
    summary:
      "[West Lake Serenity, Tea Terraces] 3-Day, 2-Night Hangzhou Cultural Retreat [...].",
    durationLabel: "3-Days 2-Nights",
    price: "599 $",
    oldPrice: "799 $",
    badge: "SALE",
    image: wukangImage
  },
  {
    id: "chengdu",
    title: "Chengdu",
    country: "China",
    summary:
      "[Pandas & Sichuan Flavors] 3-Day, 2-Night Chengdu City Break + Research Base Visit [...].",
    durationLabel: "3-Days 2-Nights",
    price: "499 $",
    oldPrice: "699 $",
    badge: "SALE",
    image: pmImage
  },
  {
    id: "lijiang",
    title: "Lijiang",
    country: "China",
    summary:
      "[Old Town Charm, Jade Dragon Snow Mountain] 5-Day, 4-Night Lijiang Discovery [...].",
    durationLabel: "5-Days 4-Nights",
    price: "899 $",
    oldPrice: "1099 $",
    badge: "SALE",
    image: shanghaiImage
  },
  {
    id: "xiamen",
    title: "Xiamen",
    country: "China",
    summary:
      "[Coastal Breeze, Gulangyu Island] 4-Day, 3-Night Xiamen Seaside Journey [...].",
    durationLabel: "4-Days 3-Nights",
    price: "649 $",
    oldPrice: "849 $",
    badge: "SALE",
    image: wukangImage
  },
  {
    id: "zhangjiajie",
    title: "Zhangjiajie",
    country: "China",
    summary:
      "[Avatar Mountains, Glass Bridge] 4-Day, 3-Night Zhangjiajie Adventure [...].",
    durationLabel: "4-Days 3-Nights",
    price: "899 $",
    oldPrice: "1199 $",
    badge: "SALE",
    image: pmImage
  },
  {
    id: "qingdao",
    title: "Qingdao",
    country: "China",
    summary:
      "[Seaside Promenade, Old Town] 3-Day, 2-Night Qingdao Coastal Escape [...].",
    durationLabel: "3-Days 2-Nights",
    price: "459 $",
    oldPrice: "599 $",
    badge: "SALE",
    image: shanghaiImage
  },
  {
    id: "suzhou",
    title: "Suzhou",
    country: "China",
    summary:
      "[Classical Gardens, Water Towns] 2-Day, 1-Night Suzhou Heritage Tour [...].",
    durationLabel: "2- Day 1-Night",
    price: "399 $",
    oldPrice: "549 $",
    badge: "SALE",
    image: wukangImage
  }
];

type TravelFilters = {
  keyword: string;
  destination: string;
  topics: string[];
  maxPrice: number | null;
  durationDays: number | null;
};

type BookingPaymentResultState = BookingPaymentResult & {
  travelId: string;
};

const parseDurationDays = (durationLabel: string) => {
  const match = durationLabel.match(/(\d+)/);
  if (!match) {
    return 1;
  }

  const parsed = Number.parseInt(match[1], 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const parsePrice = (priceLabel: string) => {
  const parsed = Number(priceLabel.replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeTravelConfig = (
  config: PublicTravelConfig
): TravelDetailConfig => {
  const rawDurationDays = Number(
    (config as { basic?: { durationDays?: number } }).basic?.durationDays
  );
  const durationDays =
    Number.isFinite(rawDurationDays) && rawDurationDays > 0
      ? Math.floor(rawDurationDays)
      : parseDurationDays(config.basic.durationLabel);
  const rawTopics = (config as { basic?: { topics?: unknown[] } }).basic?.topics;
  const topics = Array.isArray(rawTopics)
    ? rawTopics
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter(Boolean)
    : [];

  return {
    ...config,
    locale: config.locale || "en-US",
    basic: {
      ...config.basic,
      durationDays,
      topics
    }
  };
};

const normalizeBlogConfig = (config: BlogArticleConfig): BlogArticleConfig => ({
  ...config,
  locale: config.locale || "en-US",
  detail: {
    ...config.detail,
    latestSection: {
      enabled: config.detail.latestSection?.enabled ?? true,
      eyebrow: config.detail.latestSection?.eyebrow || "Travel Blog",
      title: config.detail.latestSection?.title || "Latest Articles",
      articleIds: config.detail.latestSection?.articleIds ?? []
    }
  }
});

const getBlogPublishedTime = (config: BlogArticleConfig) => {
  const parsed = new Date(config.basic.publishedAt).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const toTravelPackage = (config: TravelDetailConfig): TravelPackage => ({
  id: config.id,
  title: config.basic.title,
  destination: config.basic.destination,
  country: config.basic.country,
  summary:
    config.basic.summary ||
    "A curated travel package with flexible itinerary and premium local experiences.",
  durationDays: config.basic.durationDays,
  durationLabel: config.basic.durationLabel,
  topics: config.basic.topics,
  price: config.basic.priceFrom,
  oldPrice: "",
  badge: "LIVE",
  image: config.basic.heroImageUrl || zhangjiajieImage
});

const shopProducts = [
  {
    id: "longquan-celadon",
    title: "龙泉青瓷 · 天青釉茶具",
    price: "$139.00",
    oldPrice: "$169.00",
    badge: "促销中",
    image: wukangImage
  },
  {
    id: "jingdezhen-porcelain",
    title: "景德镇手绘瓷 · 粉彩盖碗",
    price: "$129.00",
    oldPrice: "$159.00",
    badge: "促销中",
    image: shanghaiImage
  },
  {
    id: "yixing-teapot",
    title: "宜兴紫砂壶 · 经典西施",
    price: "$189.00",
    oldPrice: "$219.00",
    badge: "促销中",
    image: pmImage
  },
  {
    id: "suzhou-embroidery",
    title: "苏绣手帕 · 兰竹小品",
    price: "$59.00",
    oldPrice: "$79.00",
    badge: "促销中",
    image: zhangjiajieImage
  },
  {
    id: "sichuan-brocade",
    title: "蜀锦丝巾 · 云锦纹样",
    price: "$99.00",
    oldPrice: "$129.00",
    badge: "促销中",
    image: shanghaiImage
  },
  {
    id: "nanjing-yunjin",
    title: "南京云锦书签 · 织金",
    price: "$39.00",
    oldPrice: "$49.00",
    badge: "促销中",
    image: wukangImage
  },
  {
    id: "cloisonne",
    title: "景泰蓝 · 迷你花瓶",
    price: "$149.00",
    oldPrice: "$179.00",
    badge: "促销中",
    image: pmImage
  },
  {
    id: "miao-silver",
    title: "苗银耳饰 · 传统錾刻",
    price: "$79.00",
    oldPrice: "$99.00",
    badge: "促销中",
    image: zhangjiajieImage
  },
  {
    id: "blue-white-plate",
    title: "青花瓷盘 · 山水纹",
    price: "$109.00",
    oldPrice: "$139.00",
    badge: "促销中",
    image: shanghaiImage
  },
  {
    id: "tea-longjing",
    title: "西湖龙井 · 明前茶",
    price: "$89.00",
    oldPrice: "$119.00",
    badge: "促销中",
    image: wukangImage
  },
  {
    id: "tea-tieguanyin",
    title: "安溪铁观音 · 清香型",
    price: "$79.00",
    oldPrice: "$99.00",
    badge: "促销中",
    image: pmImage
  },
  {
    id: "puer-tea",
    title: "云南普洱 · 熟茶饼",
    price: "$99.00",
    oldPrice: "$129.00",
    badge: "促销中",
    image: zhangjiajieImage
  },
  {
    id: "xiangyun-silk",
    title: "香云纱围巾 · 传统染",
    price: "$119.00",
    oldPrice: "$149.00",
    badge: "促销中",
    image: shanghaiImage
  },
  {
    id: "huizhou-ink",
    title: "徽墨套装 · 手工墨锭",
    price: "$69.00",
    oldPrice: "$89.00",
    badge: "促销中",
    image: wukangImage
  },
  {
    id: "paper-cut",
    title: "剪纸礼盒 · 福字窗花",
    price: "$29.00",
    oldPrice: "$39.00",
    badge: "促销中",
    image: pmImage
  },
  {
    id: "lacquerware",
    title: "脱胎漆器 · 朱漆杯",
    price: "$159.00",
    oldPrice: "$189.00",
    badge: "促销中",
    image: zhangjiajieImage
  },
  {
    id: "tibetan-thangka",
    title: "唐卡挂画 · 祥瑞图",
    price: "$179.00",
    oldPrice: "$219.00",
    badge: "促销中",
    image: shanghaiImage
  },
  {
    id: "yangzhou-lacquer",
    title: "扬州漆器 · 描金盘",
    price: "$149.00",
    oldPrice: "$189.00",
    badge: "促销中",
    image: pmImage
  },
  {
    id: "shadow-puppet",
    title: "皮影摆件 · 戏曲人物",
    price: "$49.00",
    oldPrice: "$69.00",
    badge: "促销中",
    image: zhangjiajieImage
  },
  {
    id: "bamboo-weaving",
    title: "竹编收纳 · 茶点篮",
    price: "$39.00",
    oldPrice: "$59.00",
    badge: "促销中",
    image: wukangImage
  },
  {
    id: "pingyao-lacquer",
    title: "平遥漆器 · 小盘",
    price: "$69.00",
    oldPrice: "$89.00",
    badge: "促销中",
    image: shanghaiImage
  },
  {
    id: "xiangxi-batik",
    title: "湘西蜡染 · 手工桌垫",
    price: "$45.00",
    oldPrice: "$65.00",
    badge: "促销中",
    image: pmImage
  },
  {
    id: "lantern-craft",
    title: "宫灯 · 绢面小灯",
    price: "$89.00",
    oldPrice: "$109.00",
    badge: "促销中",
    image: wukangImage
  },
  {
    id: "jade-bangle",
    title: "和田玉 · 小巧手镯",
    price: "$199.00",
    oldPrice: "$249.00",
    badge: "促销中",
    image: zhangjiajieImage
  },
  {
    id: "carved-seal",
    title: "篆刻印章 · 朱文印",
    price: "$79.00",
    oldPrice: "$99.00",
    badge: "促销中",
    image: shanghaiImage
  },
  {
    id: "paper-fan",
    title: "折扇 · 山水手绘",
    price: "$35.00",
    oldPrice: "$49.00",
    badge: "促销中",
    image: pmImage
  },
  {
    id: "miao-embroidery",
    title: "苗绣零钱包 · 花鸟",
    price: "$32.00",
    oldPrice: "$45.00",
    badge: "促销中",
    image: zhangjiajieImage
  },
  {
    id: "tangram-toy",
    title: "七巧板 · 榉木礼盒",
    price: "$19.00",
    oldPrice: "$29.00",
    badge: "促销中",
    image: wukangImage
  },
  {
    id: "xiangshan-incense",
    title: "线香 · 檀香礼盒",
    price: "$49.00",
    oldPrice: "$69.00",
    badge: "促销中",
    image: shanghaiImage
  },
  {
    id: "travel-tea-set",
    title: "功夫茶具 · 旅行便携",
    price: "$109.00",
    oldPrice: "$139.00",
    badge: "促销中",
    image: pmImage
  },
  {
    id: "silk-sachet",
    title: "香囊 · 苏绣荷包",
    price: "$29.00",
    oldPrice: "$39.00",
    badge: "促销中",
    image: wukangImage
  },
  {
    id: "woodblock-print",
    title: "木版年画 · 祈福图",
    price: "$55.00",
    oldPrice: "$75.00",
    badge: "促销中",
    image: zhangjiajieImage
  }
];

const shopProductEnTitles: Record<string, string> = {
  "longquan-celadon": "Longquan Celadon · Sky-Blue Tea Set",
  "jingdezhen-porcelain": "Jingdezhen Hand-Painted · Famille-Rose Gaiwan",
  "yixing-teapot": "Yixing Zisha Teapot · Classic Xishi",
  "suzhou-embroidery": "Suzhou Embroidery Handkerchief · Orchid & Bamboo",
  "sichuan-brocade": "Shu Brocade Silk Scarf · Yunjin Pattern",
  "nanjing-yunjin": "Nanjing Yunjin Bookmark · Woven Gold",
  cloisonne: "Cloisonne · Mini Vase",
  "miao-silver": "Miao Silver Earrings · Traditional Chasing",
  "blue-white-plate": "Blue-and-White Porcelain Plate · Landscape",
  "tea-longjing": "West Lake Longjing · Pre-Qingming",
  "tea-tieguanyin": "Anxi Tieguanyin · Light Aroma",
  "puer-tea": "Yunnan Pu'er · Ripe Tea Cake",
  "xiangyun-silk": "Xiangyunsha Scarf · Traditional Dye",
  "huizhou-ink": "Huizhou Ink Set · Handcrafted Ink Stick",
  "paper-cut": "Paper-Cut Gift Box · Fu Window Motifs",
  lacquerware: "Bodiless Lacquerware · Vermilion Cup",
  "tibetan-thangka": "Thangka Hanging · Auspicious Motif",
  "yangzhou-lacquer": "Yangzhou Lacquerware · Gilded Plate",
  "shadow-puppet": "Shadow Puppet Decor · Opera Character",
  "bamboo-weaving": "Bamboo Weaving · Tea Snack Basket",
  "pingyao-lacquer": "Pingyao Lacquerware · Small Plate",
  "xiangxi-batik": "Xiangxi Batik · Handcrafted Placemat",
  "lantern-craft": "Palace Lantern · Silk Mini Lamp",
  "jade-bangle": "Hetian Jade · Petite Bangle",
  "carved-seal": "Seal Carving · Zhu Wen Seal",
  "paper-fan": "Folding Fan · Hand-Painted Landscape",
  "miao-embroidery": "Miao Embroidery Pouch · Floral & Birds",
  "tangram-toy": "Tangram · Beechwood Gift Box",
  "xiangshan-incense": "Incense Sticks · Sandalwood Set",
  "travel-tea-set": "Gongfu Tea Set · Travel Kit",
  "silk-sachet": "Silk Sachet · Su Embroidery",
  "woodblock-print": "Woodblock New Year Print · Blessing"
};

const shopSortOptions = ["默认产品排序", "按价格：低到高", "按价格：高到低"];

const companyReasons = [
  {
    title: "A Decade of Trusted Expertise",
    icon: (
      <svg
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-12 w-12"
      >
        <circle cx="32" cy="32" r="20" stroke="#0b1c2b" strokeWidth="2.5" />
        <circle cx="32" cy="32" r="10" stroke="#f3b455" strokeWidth="2.5" />
        <path
          d="M32 20V32L40 36"
          stroke="#0b1c2b"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16 18L20 22"
          stroke="#0b1c2b"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <path
          d="M48 18L44 22"
          stroke="#0b1c2b"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    items: [
      {
        label: "Proven Track Record:",
        text:
          "With over ten years of dedicated experience, we have successfully welcomed tens of thousands of families and small groups to China."
      },
      {
        label: "Deep-Rooted Knowledge:",
        text:
          "Our long-standing presence means we have an unparalleled network and deep, insider knowledge of the country."
      },
      {
        label: "Your Peace of Mind:",
        text:
          "We anticipate needs and solve potential issues before they arise, guaranteeing a smooth experience."
      }
    ]
  },
  {
    title: "Truly Personalized Tours",
    icon: (
      <svg
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-12 w-12"
      >
        <path
          d="M16 40H48L44 20H20L16 40Z"
          stroke="#0b1c2b"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        <path
          d="M26 40V46C26 48.2091 27.7909 50 30 50H34C36.2091 50 38 48.2091 38 46V40"
          stroke="#0b1c2b"
          strokeWidth="2.5"
        />
        <path
          d="M24 24L32 28L40 24"
          stroke="#f3b455"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    items: [
      {
        label: "You are the boss:",
        text:
          "Tell us where you want to go and what you like, and we will make it happen."
      },
      {
        label: "Go beyond the tourist spots:",
        text:
          "We not only cover must-see sites but also take you to experience the real, local side of China."
      }
    ]
  },
  {
    title: "Worry-Free Travel",
    icon: (
      <svg
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-12 w-12"
      >
        <rect
          x="18"
          y="20"
          width="20"
          height="28"
          rx="3"
          stroke="#0b1c2b"
          strokeWidth="2.5"
        />
        <rect
          x="38"
          y="26"
          width="10"
          height="22"
          rx="2"
          stroke="#0b1c2b"
          strokeWidth="2.5"
        />
        <path
          d="M24 20V16H32V20"
          stroke="#f3b455"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <path
          d="M22 32H34"
          stroke="#0b1c2b"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    items: [
      {
        label: "Private & High-End Transportation:",
        text:
          "Comfortable, clean, and safe vehicles, including luxury business vans and high-end MPVs."
      },
      {
        label: "Seamless Connections:",
        text:
          "Airport pick-ups, hotel check-ins, and travel between attractions are perfectly connected."
      },
      {
        label: "Safety & Privacy First:",
        text:
          "All your travel details are kept strictly confidential for a peaceful journey."
      }
    ]
  },
  {
    title: "Our Expert Team",
    icon: (
      <svg
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-12 w-12"
      >
        <path
          d="M18 44C18 37.3726 23.3726 32 30 32H34C40.6274 32 46 37.3726 46 44V48H18V44Z"
          stroke="#0b1c2b"
          strokeWidth="2.5"
        />
        <circle cx="32" cy="22" r="8" stroke="#0b1c2b" strokeWidth="2.5" />
        <path
          d="M44 18L50 12"
          stroke="#f3b455"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <path
          d="M46 24H54"
          stroke="#f3b455"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      </svg>
    ),
    items: [
      {
        label: "Travel Designers:",
        text:
          "They are more than itinerary planners; they are true China experts."
      },
      {
        label: "Elite Tour Guides:",
        text:
          "Fluent English speakers and friendly cultural ambassadors with deep local insights."
      },
      {
        label: "Dedicated Customer Manager:",
        text:
          "A dedicated contact person ensures your needs are met instantly throughout your trip."
      }
    ]
  }
];

const companyProcessSteps = [
  {
    title: "Private Connect",
    description:
      "Exclusive luxury travel designer joins your private group one-on-one to understand your travel style, preferences, and needs."
  },
  {
    title: "Initial Inspiration Draft",
    description:
      "Present your exclusive travel blueprint within 2 hours so you can instantly see the journey shape."
  },
  {
    title: "Initiate Customization",
    description:
      "A refundable customization deposit starts the professional service process with full transparency."
  },
  {
    title: "Meticulous Crafting",
    description:
      "Present a high-end itinerary within 72 hours, refining every detail side by side."
  },
  {
    title: "Confirmation and Pricing",
    description:
      "All quotations clearly presented: hotels, flights, guides, and experience programs item by item."
  },
  {
    title: "Book with Peace of Mind",
    description:
      "One-click confirmation with global airline, hotel, and ground handling arranged in sync."
  },
  {
    title: "Private Concierge",
    description:
      "Pre-trip briefing plus full-journey real-time support for instructions and route suggestions."
  },
  {
    title: "Final Itinerary",
    description:
      "Luxury digital itinerary package and e-ticket details in one seamless plan."
  },
  {
    title: "Exclusive On-the-Ground Service",
    description:
      "Personalized reception and a dedicated driver-guide team for a smooth, worry-free journey."
  }
];

const companyPartners = [
  "Traveling",
  "Yacht Club",
  "Biking",
  "Travel World",
  "Blissful",
  "Traveling"
];

export default function App() {
  const urlSearchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const initialBlogSlug = (urlSearchParams.get("blog") || "").trim();
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeTab, setActiveTab] = useState(initialBlogSlug ? "Blog" : navItems[0]);
  const [travelPage, setTravelPage] = useState(1);
  const [travelKeywordDraft, setTravelKeywordDraft] = useState("");
  const [travelFacets, setTravelFacets] = useState<PublicTravelFacets>({
    destinations: [],
    topics: [],
    durationDays: [],
    maxPrice: 5000,
    minDurationDays: 1,
    maxDurationDays: 14
  });
  const [travelFilters, setTravelFilters] = useState<TravelFilters>({
    keyword: "",
    destination: "",
    topics: [],
    maxPrice: null,
    durationDays: null
  });
  const [publishedTravelPackages, setPublishedTravelPackages] =
    useState<TravelPackage[]>([]);
  const [homeFeaturedTravelPackages, setHomeFeaturedTravelPackages] =
    useState<TravelPackage[]>([]);
  const [isHomeFeaturedLoading, setIsHomeFeaturedLoading] = useState(true);
  const [homeFeaturedTravelConfigMapState, setHomeFeaturedTravelConfigMapState] =
    useState<Record<string, TravelDetailConfig>>({});
  const [homeSearchKeyword, setHomeSearchKeyword] = useState("");
  const [homeSearchDestination, setHomeSearchDestination] = useState("");
  const [homeSearchTopic, setHomeSearchTopic] = useState("");
  const [travelTotalPagesState, setTravelTotalPagesState] = useState(1);
  const [travelConfigMapState, setTravelConfigMapState] = useState<
    Record<string, TravelDetailConfig>
  >({});
  const [isTravelLoading, setIsTravelLoading] = useState(true);
  const [travelLoadError, setTravelLoadError] = useState<string | null>(null);
  const [selectedTravelId, setSelectedTravelId] = useState<string | null>(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingPaymentResult, setBookingPaymentResult] =
    useState<BookingPaymentResultState | null>(null);
  const [shopSort, setShopSort] = useState(shopSortOptions[0]);
  const [shopPage, setShopPage] = useState(1);
  const [blogPage, setBlogPage] = useState(1);
  const [blogPosts, setBlogPosts] = useState<BlogArticleConfig[]>([]);
  const [blogConfigMapState, setBlogConfigMapState] = useState<
    Record<string, BlogArticleConfig>
  >({});
  const [selectedBlogSlug, setSelectedBlogSlug] = useState<string | null>(
    initialBlogSlug || null
  );
  const [isBlogLoading, setIsBlogLoading] = useState(true);
  const [blogLoadError, setBlogLoadError] = useState<string | null>(null);
  const [isBlogDetailLoading, setIsBlogDetailLoading] = useState(false);
  const [footerContactPopover, setFooterContactPopover] =
    useState<FooterContactPopover>(null);
  const paypalFlow = (urlSearchParams.get("paypalFlow") || "").trim().toLowerCase();
  const paypalOrderToken = (urlSearchParams.get("token") || "").trim();
  const paymentCancelled = (urlSearchParams.get("cancelled") || "").trim() === "1";
  const balanceToken = (urlSearchParams.get("balanceToken") || "").trim();
  const isHome = activeTab === "Home";
  const isBookingPayPalReturnFlow =
    paypalFlow === "booking" && paypalOrderToken.length > 0 && !paymentCancelled;
  const isBalancePaymentFlow = balanceToken.length > 0;
  const isBookingCancelledPayFlow = paymentCancelled && paypalFlow === "booking";
  const isStandalonePaymentFlow =
    isBalancePaymentFlow || isBookingPayPalReturnFlow || isBookingCancelledPayFlow;
  const travelPageSize = 6;
  const blogPageSize = 4;
  const travelTotalPages = Math.max(1, travelTotalPagesState);
  const travelPageItems = publishedTravelPackages;
  const blogTotalPages = Math.max(1, Math.ceil(blogPosts.length / blogPageSize));
  const blogPageItems = useMemo(
    () => blogPosts.slice((blogPage - 1) * blogPageSize, blogPage * blogPageSize),
    [blogPosts, blogPage, blogPageSize]
  );
  const homeFeaturedPackages = useMemo(() => {
    if (homeFeaturedTravelPackages.length === 0) {
      return [];
    }

    return homeFeaturedTravelPackages.slice(0, 3).map((item) => ({
      id: item.id,
      title: item.title,
      country: item.country,
      summary: item.summary,
      durationLabel: item.durationLabel,
      price: item.price,
      oldPrice: item.oldPrice || "",
      badge: item.badge,
      image: item.image
    }));
  }, [homeFeaturedTravelPackages]);
  const homeKeywordHighlights = useMemo(() => {
    if (homeFeaturedTravelPackages.length === 0) {
      return fallbackHomeHighlights;
    }

    return homeFeaturedTravelPackages.slice(0, 3).map((item, index) => {
      const fallbackStyle =
        fallbackHomeHighlights[index % fallbackHomeHighlights.length];
      const keyword =
        item.topics?.find((topic) => topic.trim().length > 0) ||
        item.destination?.trim() ||
        item.title;

      return {
        ...fallbackStyle,
        id: `home-highlight-${item.id}`,
        title: keyword,
        image: item.image
      };
    });
  }, [homeFeaturedTravelPackages]);
  const homeDestinationOptions = useMemo(() => {
    if (travelFacets.destinations.length > 0) {
      return travelFacets.destinations;
    }

    return Array.from(
      new Set(homeFeaturedPackages.map((item) => item.title.trim()).filter(Boolean))
    ).sort((a, b) => a.localeCompare(b));
  }, [homeFeaturedPackages, travelFacets.destinations]);
  const homeTopicOptions = useMemo(() => {
    if (travelFacets.topics.length > 0) {
      return travelFacets.topics;
    }

    return Array.from(
      new Set(
        homeFeaturedTravelPackages
          .flatMap((item) => item.topics ?? [])
          .map((topic) => topic.trim())
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [homeFeaturedTravelPackages, travelFacets.topics]);

  const shopPageSize = 16;
  const shopSortedProducts = useMemo(() => {
    if (shopSort === shopSortOptions[0]) {
      return shopProducts;
    }
    const sorted = [...shopProducts];
    const toPrice = (value: string) => Number(value.replace(/[^0-9.]/g, ""));
    sorted.sort((a, b) => toPrice(a.price) - toPrice(b.price));
    if (shopSort === shopSortOptions[2]) {
      sorted.reverse();
    }
    return sorted;
  }, [shopSort]);
  const shopTotalPages = Math.max(
    1,
    Math.ceil(shopSortedProducts.length / shopPageSize)
  );
  const shopPageItems = shopSortedProducts.slice(
    (shopPage - 1) * shopPageSize,
    shopPage * shopPageSize
  );

  const adaptTravelDetailConfig = (
    config: TravelDetailConfig
  ): TravelDetailData => {
    const relatedTrips = config.sections.relatedTrips
      ? {
          enabled: config.sections.relatedTrips.enabled,
          title: config.sections.relatedTrips.title,
          items: config.sections.relatedTrips.tripIds
            .map((id) => {
              const match = publishedTravelPackages.find((item) => item.id === id);
              if (!match) {
                return null;
              }
              return {
                id: match.id,
                title: match.title,
                country: match.country,
                durationLabel: match.durationLabel,
                priceFrom: match.price,
                imageUrl: match.image
              };
            })
            .filter((item): item is NonNullable<typeof item> => item !== null)
        }
      : undefined;

    return {
      ...config,
      sections: {
        ...config.sections,
        relatedTrips
      }
    };
  };

  const selectedTravelConfig = useMemo(
    () =>
      selectedTravelId
        ? travelConfigMapState[selectedTravelId] ??
          homeFeaturedTravelConfigMapState[selectedTravelId] ??
          null
        : null,
    [selectedTravelId, travelConfigMapState, homeFeaturedTravelConfigMapState]
  );

  const travelDetailData = useMemo(
    () =>
      selectedTravelConfig ? adaptTravelDetailConfig(selectedTravelConfig) : null,
    [selectedTravelConfig, publishedTravelPackages]
  );
  const activeBookingPaymentResult =
    travelDetailData && bookingPaymentResult?.travelId === travelDetailData.id
      ? bookingPaymentResult
      : null;
  const selectedBlogConfig = useMemo(
    () => (selectedBlogSlug ? blogConfigMapState[selectedBlogSlug] ?? null : null),
    [selectedBlogSlug, blogConfigMapState]
  );
  const latestBlogArticles = useMemo(() => {
    if (!selectedBlogConfig) {
      return [];
    }

    const explicitIds = selectedBlogConfig.detail.latestSection.articleIds;
    const explicitItems = explicitIds
      .map((key) =>
        blogPosts.find((item) => item.id === key || item.slug === key) ?? null
      )
      .filter((item): item is BlogArticleConfig => Boolean(item))
      .filter((item) => item.slug !== selectedBlogConfig.slug);

    if (explicitItems.length > 0) {
      return explicitItems.slice(0, 3);
    }

    return blogPosts
      .filter((item) => item.slug !== selectedBlogConfig.slug)
      .slice(0, 3);
  }, [selectedBlogConfig, blogPosts]);

  const loadPublishedTravels = useCallback(async (filters: TravelFilters, page: number) => {
    setIsTravelLoading(true);
    setTravelLoadError(null);
    try {
      const params = new URLSearchParams();
      const keyword = filters.keyword.trim();
      const destination = filters.destination.trim();

      if (keyword) {
        params.set("q", keyword);
      }
      if (destination) {
        params.set("destination", destination);
      }
      if (filters.topics.length > 0) {
        params.set("topics", filters.topics.join(","));
      }
      if (typeof filters.maxPrice === "number") {
        params.set("maxPrice", String(filters.maxPrice));
      }
      if (typeof filters.durationDays === "number") {
        params.set("durationDays", String(filters.durationDays));
      }
      params.set("page", String(page));
      params.set("pageSize", String(travelPageSize));

      const query = params.toString();
      const response = await fetch(
        `${TRAVEL_API_BASE_URL}/api/public/travels${query ? `?${query}` : ""}`
      );
      const payload = (await response
        .json()
        .catch(() => null)) as PublicTravelListResponse | null;

      if (!response.ok || !payload?.success || !payload.data) {
        throw new Error(
          payload?.error || `Failed to load published travels (${response.status}).`
        );
      }

      const nextConfigs = payload.data.items
        .map((item) => normalizeTravelConfig(item.config))
        .filter((config) => config.status === "published");
      const nextPagination = payload.data.pagination;

      const fallbackFacets: PublicTravelFacets = {
        destinations: Array.from(
          new Set(nextConfigs.map((config) => config.basic.destination.trim()).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b)),
        topics: Array.from(
          new Set(nextConfigs.flatMap((config) => config.basic.topics ?? []).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b)),
        durationDays: Array.from(
          new Set(nextConfigs.map((config) => config.basic.durationDays))
        ).sort((a, b) => a - b),
        maxPrice: Math.max(
          5000,
          ...nextConfigs.map((config) => parsePrice(config.basic.priceFrom))
        ),
        minDurationDays: nextConfigs.length
          ? Math.min(...nextConfigs.map((config) => config.basic.durationDays))
          : 1,
        maxDurationDays: nextConfigs.length
          ? Math.max(...nextConfigs.map((config) => config.basic.durationDays))
          : 14
      };

      const nextFacets: PublicTravelFacets = {
        ...fallbackFacets,
        ...(payload.data.facets ?? {}),
        durationDays:
          payload.data.facets?.durationDays && payload.data.facets.durationDays.length > 0
            ? payload.data.facets.durationDays
            : fallbackFacets.durationDays
      };

      const nextConfigMap = Object.fromEntries(
        nextConfigs.map((config) => [config.id, config])
      ) as Record<string, TravelDetailConfig>;

      setTravelConfigMapState(nextConfigMap);
      setPublishedTravelPackages(nextConfigs.map(toTravelPackage));
      setTravelTotalPagesState(nextPagination?.totalPages ?? 1);
      if (nextPagination?.page && nextPagination.page !== page) {
        setTravelPage(nextPagination.page);
      }
      setTravelFacets(nextFacets);
      setTravelFilters((current) => {
        const nextMaxPrice =
          typeof current.maxPrice === "number"
            ? Math.min(current.maxPrice, nextFacets.maxPrice)
            : current.maxPrice;
        const nextTopics = current.topics.filter((topic) =>
          nextFacets.topics.includes(topic)
        );
        const nextDestination = nextFacets.destinations.includes(current.destination)
          ? current.destination
          : "";
        const nextDurationDays =
          typeof current.durationDays === "number" &&
          !nextFacets.durationDays.includes(current.durationDays)
            ? null
            : current.durationDays;

        const unchanged =
          current.maxPrice === nextMaxPrice &&
          current.destination === nextDestination &&
          current.durationDays === nextDurationDays &&
          current.topics.length === nextTopics.length &&
          current.topics.every((topic, index) => topic === nextTopics[index]);

        if (unchanged) {
          return current;
        }

        return {
          ...current,
          maxPrice: nextMaxPrice,
          topics: nextTopics,
          destination: nextDestination,
          durationDays: nextDurationDays
        };
      });
    } catch (error) {
      console.error("Failed to fetch published travels from backend.", error);
      setTravelConfigMapState({});
      setPublishedTravelPackages([]);
      setTravelTotalPagesState(1);
      setTravelFacets({
        destinations: [],
        topics: [],
        durationDays: [],
        maxPrice: 5000,
        minDurationDays: 1,
        maxDurationDays: 14
      });
      setTravelLoadError(
        error instanceof Error
          ? error.message
          : "Failed to load published travels from backend."
      );
    } finally {
      setIsTravelLoading(false);
    }
  }, [travelPageSize]);

  const loadHomeFeaturedTravels = useCallback(async () => {
    setIsHomeFeaturedLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("page", "1");
      params.set("pageSize", "3");
      const response = await fetch(
        `${TRAVEL_API_BASE_URL}/api/public/travels?${params.toString()}`
      );
      const payload = (await response
        .json()
        .catch(() => null)) as PublicTravelListResponse | null;

      if (!response.ok || !payload?.success || !payload.data) {
        throw new Error(
          payload?.error ||
            `Failed to load featured travels for home page (${response.status}).`
        );
      }

      const nextConfigs = payload.data.items
        .map((item) => normalizeTravelConfig(item.config))
        .filter((config) => config.status === "published");
      const nextConfigMap = Object.fromEntries(
        nextConfigs.map((config) => [config.id, config])
      ) as Record<string, TravelDetailConfig>;

      setHomeFeaturedTravelConfigMapState(nextConfigMap);
      setHomeFeaturedTravelPackages(nextConfigs.map(toTravelPackage));
    } catch (error) {
      console.error("Failed to fetch home featured travels from backend.", error);
      setHomeFeaturedTravelConfigMapState({});
      setHomeFeaturedTravelPackages([]);
    } finally {
      setIsHomeFeaturedLoading(false);
    }
  }, []);

  const loadPublishedBlogs = useCallback(async () => {
    setIsBlogLoading(true);
    setBlogLoadError(null);
    try {
      const response = await fetch(`${TRAVEL_API_BASE_URL}/api/public/blogs?page=1&pageSize=24`);
      const payload = (await response
        .json()
        .catch(() => null)) as PublicBlogListResponse | null;

      if (!response.ok || !payload?.success || !payload.data) {
        throw new Error(payload?.error || `Failed to load blog articles (${response.status}).`);
      }

      const nextBlogs = payload.data.items
        .map((item) => normalizeBlogConfig(item.config))
        .filter((item) => item.status === "published")
        .sort((a, b) => getBlogPublishedTime(b) - getBlogPublishedTime(a));

      setBlogPosts(nextBlogs);
      setBlogConfigMapState(
        Object.fromEntries(nextBlogs.map((item) => [item.slug, item])) as Record<
          string,
          BlogArticleConfig
        >
      );
    } catch (error) {
      console.error("Failed to fetch published blogs from backend.", error);
      setBlogPosts([]);
      setBlogConfigMapState({});
      setBlogLoadError(
        error instanceof Error ? error.message : "Failed to load blog articles."
      );
    } finally {
      setIsBlogLoading(false);
    }
  }, []);

  const loadBlogDetail = useCallback(async (slug: string) => {
    if (!slug) {
      return;
    }

    setIsBlogDetailLoading(true);
    try {
      const response = await fetch(
        `${TRAVEL_API_BASE_URL}/api/public/blogs/${encodeURIComponent(slug)}`
      );
      const payload = (await response
        .json()
        .catch(() => null)) as PublicBlogDetailResponse | null;

      if (!response.ok || !payload?.success || !payload.data) {
        throw new Error(payload?.error || `Failed to load blog detail (${response.status}).`);
      }

      const nextConfig = normalizeBlogConfig(payload.data.item.config);
      setBlogConfigMapState((current) => ({
        ...current,
        [nextConfig.slug]: nextConfig
      }));
      setBlogPosts((current) => {
        const filtered = current.filter((item) => item.slug !== nextConfig.slug);
        return [...filtered, nextConfig].sort(
          (a, b) => getBlogPublishedTime(b) - getBlogPublishedTime(a)
        );
      });
    } catch (error) {
      console.error("Failed to fetch blog detail from backend.", error);
      setBlogLoadError(error instanceof Error ? error.message : "Failed to load blog detail.");
    } finally {
      setIsBlogDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void loadPublishedTravels(travelFilters, travelPage);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [loadPublishedTravels, travelFilters, travelPage]);

  useEffect(() => {
    void loadHomeFeaturedTravels();
  }, [loadHomeFeaturedTravels]);

  useEffect(() => {
    void loadPublishedBlogs();
  }, [loadPublishedBlogs]);

  useEffect(() => {
    if (!selectedBlogSlug || blogConfigMapState[selectedBlogSlug]) {
      return;
    }

    void loadBlogDetail(selectedBlogSlug);
  }, [selectedBlogSlug, blogConfigMapState, loadBlogDetail]);

  useEffect(() => {
    if (!isHome) {
      return;
    }

    const interval = window.setInterval(() => {
      setActiveIndex((index) => (index + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(interval);
  }, [isHome]);

  useEffect(() => {
    if (travelPage > travelTotalPages) {
      setTravelPage(travelTotalPages);
    }
  }, [travelPage, travelTotalPages]);

  useEffect(() => {
    if (shopPage > shopTotalPages) {
      setShopPage(shopTotalPages);
    }
  }, [shopPage, shopTotalPages]);

  useEffect(() => {
    if (blogPage > blogTotalPages) {
      setBlogPage(blogTotalPages);
    }
  }, [blogPage, blogTotalPages]);

  useEffect(() => {
    if (
      selectedTravelId &&
      !travelConfigMapState[selectedTravelId] &&
      !homeFeaturedTravelConfigMapState[selectedTravelId]
    ) {
      setSelectedTravelId(null);
      setBookingOpen(false);
      setBookingPaymentResult(null);
    }
  }, [selectedTravelId, travelConfigMapState, homeFeaturedTravelConfigMapState]);

  useEffect(() => {
    if (!selectedBlogSlug || blogPosts.length === 0 || blogConfigMapState[selectedBlogSlug]) {
      return;
    }

    const existsInList = blogPosts.some((item) => item.slug === selectedBlogSlug);
    if (!existsInList && !isBlogDetailLoading) {
      setSelectedBlogSlug(null);
    }
  }, [selectedBlogSlug, blogPosts, blogConfigMapState, isBlogDetailLoading]);

  useEffect(() => {
    if (
      homeSearchDestination &&
      !homeDestinationOptions.includes(homeSearchDestination)
    ) {
      setHomeSearchDestination("");
    }
  }, [homeDestinationOptions, homeSearchDestination]);

  useEffect(() => {
    if (homeSearchTopic && !homeTopicOptions.includes(homeSearchTopic)) {
      setHomeSearchTopic("");
    }
  }, [homeSearchTopic, homeTopicOptions]);

  const currentSlide = slides[activeIndex];
  const heroContent = slides[0];

  const syncBlogUrl = (slug: string | null) => {
    const url = new URL(window.location.href);
    if (slug) {
      url.searchParams.set("blog", slug);
    } else {
      url.searchParams.delete("blog");
    }
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  };

  const handleNavClick = (item: string) => {
    setActiveTab(item);
    setSelectedTravelId(null);
    if (item !== "Blog") {
      setSelectedBlogSlug(null);
      syncBlogUrl(null);
    }
    setBookingOpen(false);
    setBookingPaymentResult(null);
    if (item === "Travel") {
      setTravelPage(1);
    }
    if (item === "Shop") {
      setShopPage(1);
    }
    if (item === "Blog") {
      setBlogPage(1);
    }
  };

  const handleSelectBlog = (slug: string) => {
    setActiveTab("Blog");
    setSelectedTravelId(null);
    setBookingOpen(false);
    setBookingPaymentResult(null);
    setSelectedBlogSlug(slug);
    syncBlogUrl(slug);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToBlogList = () => {
    setSelectedBlogSlug(null);
    syncBlogUrl(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleHomeMoreInfo = () => {
    handleNavClick("Travel");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleHomeContactUs = () => {
    handleNavClick("Contact Us");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFooterContactClick = (target: Exclude<FooterContactPopover, null>) => {
    setFooterContactPopover((current) => (current === target ? null : target));
  };

  const handleFooterTikTokClick = () => {
    setFooterContactPopover(null);
    window.open("https://www.tiktok.com/@fulcrumtravel", "_blank", "noopener,noreferrer");
  };

  const handleHomeSearch = () => {
    const keyword = homeSearchKeyword.trim();
    handleNavClick("Travel");
    setTravelKeywordDraft(keyword);
    setTravelFilters({
      keyword,
      destination: homeSearchDestination,
      topics: homeSearchTopic ? [homeSearchTopic] : [],
      maxPrice: null,
      durationDays: null
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleHomeDestinationDetails = (id: string) => {
    const hasConfig = Boolean(
      travelConfigMapState[id] || homeFeaturedTravelConfigMapState[id]
    );

    if (!hasConfig) {
      handleHomeMoreInfo();
      return;
    }

    handleNavClick("Travel");
    setTravelKeywordDraft("");
    setTravelFilters({
      keyword: "",
      destination: "",
      topics: [],
      maxPrice: null,
      durationDays: null
    });
    setSelectedTravelId(id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSelectTravel = (id: string) => {
    setSelectedTravelId(id);
    setBookingOpen(false);
    setBookingPaymentResult(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleTravelKeywordChange = (value: string) => {
    setTravelKeywordDraft(value);
  };

  const handleApplyTravelKeywordSearch = () => {
    setTravelPage(1);
    setTravelFilters((current) => ({
      ...current,
      keyword: travelKeywordDraft.trim()
    }));
  };

  const handleTravelDestinationChange = (value: string) => {
    setTravelPage(1);
    setTravelFilters((current) => ({
      ...current,
      destination: value
    }));
  };

  const handleTravelDurationDaysChange = (value: number | null) => {
    setTravelPage(1);
    setTravelFilters((current) => ({
      ...current,
      durationDays: value
    }));
  };

  const handleTravelMaxPriceChange = (value: number) => {
    setTravelPage(1);
    setTravelFilters((current) => ({
      ...current,
      maxPrice: value
    }));
  };

  const handleToggleTravelTopic = (topic: string) => {
    setTravelPage(1);
    setTravelFilters((current) => {
      const existed = current.topics.includes(topic);
      return {
        ...current,
        topics: existed
          ? current.topics.filter((item) => item !== topic)
          : [...current.topics, topic]
      };
    });
  };

  const handleBookNow = () => {
    setBookingOpen(true);
    setBookingPaymentResult(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#f5f8fb] text-[#101722] font-sans">
      {!isStandalonePaymentFlow && (
        <header className="flex flex-col items-start gap-4 px-6 py-4 md:grid md:grid-cols-[1fr_auto_1fr] md:items-center md:gap-6 md:px-12">
        <div className="flex items-center gap-4 md:justify-self-start">
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt="Fulcrum Travel logo"
              className="h-10 w-auto object-contain"
            />
          </div>
        </div>

        <nav className="hidden md:flex md:justify-self-center" aria-label="Primary">
          <ul className="flex items-center text-[15px] font-normal tracking-[0.02em] text-[#1f2b3b]">
            {navItems.map((item, index) => (
              <li key={item} className="flex items-center">
                <button
                  type="button"
                  onClick={() => handleNavClick(item)}
                  aria-current={activeTab === item ? "page" : undefined}
                  className={`transition hover:text-[#d18400] ${
                    activeTab === item ? "text-[#d18400]" : ""
                  }`}
                >
                  {item}
                </button>
                {index < navItems.length - 1 && (
                  <span className="mx-5 h-1 w-1 rounded-full bg-[#f3b455]" />
                )}
              </li>
            ))}
          </ul>
        </nav>

        <div className="w-full md:hidden">
          <label htmlFor="mobile-nav" className="sr-only">
            Navigation
          </label>
          <select
            id="mobile-nav"
            value={activeTab}
            onChange={(event) => handleNavClick(event.target.value)}
            className="h-11 w-full rounded-xl border border-[#e2e8f0] bg-white px-4 text-sm text-[#1f2b3b] shadow-[0_10px_24px_rgba(15,23,36,0.08)] outline-none transition focus:border-[#0d5be1]"
          >
            {navItems.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>
        </div>

        <button
          className="w-full rounded-xl bg-[#0d5be1] px-6 py-3 text-sm font-normal text-white shadow-[0_12px_24px_rgba(13,91,225,0.22)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_28px_rgba(13,91,225,0.3)] md:w-auto md:justify-self-end"
          type="button"
          onClick={() => handleNavClick("Travel")}
        >
          Search Travel
        </button>
        </header>
      )}

      <main>
        {isBookingPayPalReturnFlow ? (
          <BookingPayPalReturnPage
            apiBaseUrl={TRAVEL_API_BASE_URL}
            orderId={paypalOrderToken}
          />
        ) : isBalancePaymentFlow ? (
          <BalancePaymentPage
            apiBaseUrl={TRAVEL_API_BASE_URL}
            paypalClientId={PAYPAL_CLIENT_ID}
            token={balanceToken}
            paypalOrderId={paypalFlow === "balance" ? paypalOrderToken : ""}
            paymentCancelled={paymentCancelled && paypalFlow === "balance"}
          />
        ) : isBookingCancelledPayFlow ? (
          <section className="mx-auto w-full max-w-[920px] px-4 py-10 md:px-8 lg:px-10">
            <div className="rounded-3xl border border-[#fde68a] bg-[#fffbeb] p-8 text-[#92400e] shadow-[0_18px_40px_rgba(15,23,36,0.12)]">
              <p className="text-xs font-semibold uppercase tracking-[0.32em] text-[#f59e0b]">
                Payment Cancelled
              </p>
              <h1 className="mt-3 text-2xl font-semibold text-[#78350f] md:text-3xl">
                You cancelled this PayPal payment.
              </h1>
              <p className="mt-3 text-sm text-[#92400e]">
                No charge was made. You can go back and submit the booking payment again.
              </p>
              <button
                type="button"
                onClick={() => {
                  window.location.href = "/";
                }}
                className="mt-6 rounded-xl border border-[#d1d5db] bg-white px-5 py-3 text-sm font-semibold text-[#1f2937] transition hover:border-[#0d5be1] hover:text-[#0d5be1]"
              >
                Back to home
              </button>
            </div>
          </section>
        ) : isHome ? (
          <HomeTab
            activeIndex={activeIndex}
            slides={slides}
            heroContent={heroContent}
            contacts={contacts}
            homeHighlights={homeKeywordHighlights}
            homeDestinations={homeFeaturedPackages}
            homeDestinationsLoading={isHomeFeaturedLoading}
            homeWorldwideDestinations={homeWorldwideDestinations}
            adventureHighlights={adventureHighlights}
            onMoreInfoClick={handleHomeContactUs}
            homeSearchKeyword={homeSearchKeyword}
            onHomeSearchKeywordChange={setHomeSearchKeyword}
            homeSearchDestination={homeSearchDestination}
            homeDestinationOptions={homeDestinationOptions}
            onHomeSearchDestinationChange={setHomeSearchDestination}
            homeSearchTopic={homeSearchTopic}
            homeTopicOptions={homeTopicOptions}
            onHomeSearchTopicChange={setHomeSearchTopic}
            onHomeSearchSubmit={handleHomeSearch}
            onHomeDestinationSelect={handleHomeDestinationDetails}
            onAllServicesClick={handleHomeMoreInfo}
          />
        ) : activeTab === "Travel" ? (
          travelDetailData ? (
            activeBookingPaymentResult ? (
              <BookingPaymentResultPage
                travelTitle={travelDetailData.basic.title}
                travelDestination={travelDetailData.basic.destination}
                result={activeBookingPaymentResult}
                onBackToDetails={() => {
                  setBookingPaymentResult(null);
                  setBookingOpen(false);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                onViewAllTravels={() => {
                  setBookingPaymentResult(null);
                  setBookingOpen(false);
                  setSelectedTravelId(null);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            ) : bookingOpen ? (
              <BookingPage
                travel={travelDetailData}
                apiBaseUrl={TRAVEL_API_BASE_URL}
                turnstileSiteKey={TURNSTILE_SITE_KEY}
                paypalClientId={PAYPAL_CLIENT_ID}
                onPaymentResolved={(result) => {
                  setBookingPaymentResult({
                    ...result,
                    travelId: travelDetailData.id
                  });
                  setBookingOpen(false);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                onBack={() => {
                  setBookingOpen(false);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            ) : (
              <TravelDetailPage
                data={travelDetailData}
                onSelectAttraction={handleSelectTravel}
                onBack={() => {
                  setSelectedTravelId(null);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                onBook={handleBookNow}
              />
            )
          ) : (
            <TravelTab
              keyword={travelKeywordDraft}
              onKeywordChange={handleTravelKeywordChange}
              onKeywordSearch={handleApplyTravelKeywordSearch}
              destination={travelFilters.destination}
              destinationOptions={travelFacets.destinations}
              onDestinationChange={handleTravelDestinationChange}
              selectedTopics={travelFilters.topics}
              topicOptions={travelFacets.topics}
              onToggleTopic={handleToggleTravelTopic}
              durationDays={travelFilters.durationDays}
              durationOptions={travelFacets.durationDays}
              onDurationDaysChange={handleTravelDurationDaysChange}
              maxPrice={travelFilters.maxPrice}
              maxPriceLimit={travelFacets.maxPrice}
              onMaxPriceChange={handleTravelMaxPriceChange}
              travelPageItems={travelPageItems}
              travelPage={travelPage}
              travelTotalPages={travelTotalPages}
              setTravelPage={setTravelPage}
              onSelectPackage={handleSelectTravel}
              isLoading={isTravelLoading}
              loadError={travelLoadError}
              onRetryLoad={() => {
                void loadPublishedTravels(travelFilters, travelPage);
              }}
            />
          )
        ) : activeTab === "Company" ? (
          <CompanyTab
            companyReasons={companyReasons}
            companyProcessSteps={companyProcessSteps}
            companyPartners={companyPartners}
            apiBaseUrl={TRAVEL_API_BASE_URL}
            turnstileSiteKey={TURNSTILE_SITE_KEY}
            onContactUs={() => handleNavClick("Contact Us")}
          />
        ) : activeTab === "Contact Us" ? (
          <ContactTab
            contactUsCards={contactUsCards}
            companyPartners={companyPartners}
            apiBaseUrl={TRAVEL_API_BASE_URL}
            turnstileSiteKey={TURNSTILE_SITE_KEY}
          />
        ) : activeTab === "Shop" ? (
          <ShopTab
            shopSort={shopSort}
            setShopSort={setShopSort}
            shopSortOptions={shopSortOptions}
            shopPageItems={shopPageItems}
            shopProductEnTitles={shopProductEnTitles}
            shopPage={shopPage}
            shopTotalPages={shopTotalPages}
            setShopPage={setShopPage}
          />
        ) : activeTab === "Blog" ? (
          selectedBlogConfig ? (
            <BlogDetailPage
              data={selectedBlogConfig}
              latestArticles={latestBlogArticles}
              onSelectBlog={handleSelectBlog}
              onBack={handleBackToBlogList}
            />
          ) : (
            <BlogTab
              blogPosts={blogPageItems}
              blogPage={blogPage}
              blogTotalPages={blogTotalPages}
              setBlogPage={setBlogPage}
              isLoading={isBlogLoading || isBlogDetailLoading}
              loadError={blogLoadError}
              onRetryLoad={() => {
                void loadPublishedBlogs();
              }}
              onSelectBlog={handleSelectBlog}
            />
          )
        ) : (
          <section className="mx-4 mb-6 min-h-[70vh] rounded-[32px] border border-[#e2e8f0] bg-white/80 px-6 py-16 text-center shadow-[0_24px_60px_rgba(15,23,36,0.08)] md:mx-12 md:mb-12 md:min-h-[calc(100vh-96px)] md:px-10 lg:px-16">
            <p className="text-xs font-normal uppercase tracking-[0.32em] text-[#9aa3af]">
              Coming Soon
            </p>
            <h2 className="mt-3 font-display text-3xl text-[#0b1c2b] md:text-5xl">
              {activeTab}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-[#5f6b7a] md:text-lg">
              This tab is ready for new content. We will keep everything in place
              while you decide what goes here.
            </p>
          </section>
        )}
      </main>

      {!isStandalonePaymentFlow && (
        <>
          <footer className="relative overflow-hidden bg-[#0a1d46] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_left,rgba(32,72,140,0.22),transparent_55%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(9,25,64,0.96)_0%,rgba(7,20,55,0.92)_50%,rgba(6,18,48,0.9)_100%)]" />
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: "url('/china-2506476_1920.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        />

        <div className="relative mx-4 grid gap-10 px-6 py-16 md:mx-12 md:grid-cols-[1.4fr_1fr_1fr] md:gap-12 md:px-10 lg:px-16">
          <div>
            <h3 className="footer-headline text-3xl font-semibold leading-tight md:text-5xl">
              Travel beyond your imagination, with our Travel Agency!
            </h3>
          </div>

          <div className="space-y-4 text-sm text-white/90 md:text-base">
            <p className="text-base font-semibold text-white md:text-lg">Address</p>
            <p>
              Room 606, 6th Floor, Debi WE, No. 168, Tianfu 5th Street,
              High-tech Zone, Chengdu City, Sichuan Province, China
            </p>
            <p>Email: fulcrumtravel@gmail.com</p>
            <div className="relative pt-2">
              {footerContactPopover && (
                <div className="mb-3 flex w-full max-w-[320px] items-center justify-between gap-4 rounded-2xl bg-black px-5 py-4 text-base text-white shadow-[0_16px_40px_rgba(0,0,0,0.35)]">
                  <span>
                    {footerContactPopover === "wechat"
                      ? "WeChat :18030701436"
                      : "Phone :+86 18030701436"}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFooterContactPopover(null)}
                    className="shrink-0 text-xl leading-none text-white/90 transition hover:text-white"
                    aria-label="Close contact popup"
                  >
                    ×
                  </button>
                </div>
              )}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => handleFooterContactClick("wechat")}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-[#2dd16f] transition hover:bg-white/25"
                  aria-label="Show WeChat"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                  >
                    <path d="M10.0014 14.6757C10.0011 14.6551 10.001 14.6345 10.001 14.6138C10.001 12.1055 12.0175 9.99564 14.7539 9.38092C14.3904 7.07873 11.9602 5.19995 8.90098 5.19995C5.58037 5.19995 3.00098 7.41344 3.00098 9.9793C3.00098 10.9487 3.36131 11.88 4.04082 12.6781C4.0728 12.7157 4.12443 12.7717 4.19342 12.8427C4.78537 13.4517 5.13709 14.2457 5.19546 15.0805C5.90857 14.6683 6.74285 14.5123 7.55832 14.6392C7.72416 14.665 7.85986 14.6847 7.96345 14.6982C8.27111 14.7383 8.58419 14.7586 8.90098 14.7586C9.27825 14.7586 9.64595 14.7301 10.0014 14.6757ZM10.4581 16.627C9.95467 16.7133 9.43399 16.7586 8.90098 16.7586C8.49441 16.7586 8.09502 16.7323 7.70499 16.6815C7.58312 16.6656 7.4317 16.6436 7.25073 16.6154C6.87693 16.5572 6.49436 16.6321 6.1713 16.8268L4.26653 17.9745C4.12052 18.0646 3.94891 18.1057 3.77733 18.0916C3.33814 18.0554 3.01178 17.6744 3.04837 17.2405L3.19859 15.4596C3.23664 15.0086 3.07664 14.5632 2.75931 14.2367C2.66182 14.1364 2.5814 14.0491 2.51802 13.9747C1.56406 12.8542 1.00098 11.4732 1.00098 9.9793C1.00098 6.23517 4.53793 3.19995 8.90098 3.19995C12.9601 3.19995 16.3041 5.82699 16.7504 9.20788C20.1225 9.36136 22.801 11.723 22.801 14.6138C22.801 15.8068 22.3448 16.9097 21.572 17.8044C21.5206 17.8639 21.4555 17.9336 21.3765 18.0137C21.1194 18.2744 20.9898 18.6301 21.0206 18.9903L21.1423 20.4125C21.172 20.759 20.9076 21.0632 20.5518 21.0921C20.4128 21.1034 20.2738 21.0706 20.1555 20.9986L18.6124 20.0821C18.3506 19.9266 18.0407 19.8668 17.7379 19.9133C17.5913 19.9358 17.4686 19.9533 17.3699 19.966C17.0539 20.0066 16.7303 20.0277 16.401 20.0277C13.7074 20.0277 11.4025 18.6201 10.4581 16.627ZM17.4346 17.9364C18.0019 17.8494 18.5793 17.911 19.1105 18.1111C19.2492 17.5503 19.5373 17.0304 19.9524 16.6094C20.0027 16.5585 20.0388 16.5198 20.0584 16.4971C20.5467 15.9318 20.801 15.2839 20.801 14.6138C20.801 12.8095 18.8983 11.2 16.401 11.2C13.9037 11.2 12.001 12.8095 12.001 14.6138C12.001 16.4181 13.9037 18.0277 16.401 18.0277C16.6424 18.0277 16.8809 18.0124 17.115 17.9823C17.1957 17.972 17.3029 17.9566 17.4346 17.9364Z" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => handleFooterContactClick("phone")}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-[#2dd16f] transition hover:bg-white/25"
                  aria-label="Show phone"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 16 16"
                    className="h-4 w-4"
                  >
                    <path d="M13.601 2.326A7.85 7.85 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.9 7.9 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.9 7.9 0 0 0 13.6 2.326zM7.994 14.521a6.6 6.6 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.56 6.56 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592m3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.73.73 0 0 0-.529.247c-.182.198-.691.677-.691 1.654s.71 1.916.81 2.049c.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={handleFooterTikTokClick}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-[#2dd16f] transition hover:bg-white/25"
                  aria-label="Open TikTok"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                  >
                    <path d="M16 8.24537V15.5C16 19.0899 13.0899 22 9.5 22C5.91015 22 3 19.0899 3 15.5C3 11.9101 5.91015 9 9.5 9C10.0163 9 10.5185 9.06019 11 9.17393V12.3368C10.5454 12.1208 10.0368 12 9.5 12C7.567 12 6 13.567 6 15.5C6 17.433 7.567 19 9.5 19C11.433 19 13 17.433 13 15.5V2H16C16 4.76142 18.2386 7 21 7V10C19.1081 10 17.3696 9.34328 16 8.24537Z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-4 text-sm text-white/90 md:text-base">
            <p className="text-base font-semibold text-white md:text-lg">Contact</p>
            <button
              type="button"
              className="inline-flex items-center rounded-lg bg-[#f3b455] px-5 py-2 text-sm font-semibold text-[#1d1d1d] shadow-[0_10px_20px_rgba(243,180,85,0.35)]"
            >
              fulcrumtravel@gmail.com
            </button>
            <p className="text-xl font-semibold text-white md:text-2xl">
              +86 17318902873
            </p>
          </div>
        </div>

        <div className="relative mx-4 border-t border-white/10 px-6 py-6 text-xs text-white/70 md:mx-12 md:px-10 lg:px-16">
          © 2025 FulcrumTrip. All Rights Reserved.
        </div>
          </footer>

          <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        <button
          type="button"
          onClick={() => {
            window.open(WHATSAPP_CHAT_URL, "_blank", "noopener,noreferrer");
          }}
          className="flex items-center gap-2 rounded-full bg-[#f7b500] px-5 py-3 text-xs font-semibold text-[#1d1d1d] shadow-[0_16px_30px_rgba(247,181,0,0.35)]"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/70 text-[#0b1c2b]">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
            >
              <path
                d="M5 17L4 21V6C4 4.89543 4.89543 4 6 4H18C19.1046 4 20 4.89543 20 6V14C20 15.1046 19.1046 16 18 16H8L5 17Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          WhatsApp Chat
        </button>
          </div>
        </>
      )}
    </div>
  );
}

